import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Italic, Eyebrow } from "./_primitives";
import { FUNNEL_STAGES, SCREENING_CRITERIA } from "../../data/sparkseed";
import { Check, Filter } from "lucide-react";

/* ---------- Count-up hook ---------- */
function useCountUp(target, trigger, duration = 1600, startFraction = 0) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!trigger) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(target * (startFraction + (1 - startFraction) * eased)));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setVal(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, trigger, duration, startFraction]);
  return val;
}

/* ---------- Animated flow particle ---------- */
const FlowParticle = ({ delay = 0, dur = 4 }) => (
  <span
    className="absolute left-1/2 -top-1 h-1.5 w-1.5 rounded-full bg-[#B8D958]"
    style={{
      boxShadow: "0 0 6px #B8D958, 0 0 12px rgba(184,217,88,0.6)",
      transform: "translateX(-50%)",
      animation: `ss-funnel-flow ${dur}s linear ${delay}s infinite`,
      opacity: 0,
    }}
  />
);

/* ---------- Single funnel stage ---------- */
const FunnelStage = ({ stage, i, total, inView, conversion }) => {
  // Widths from 100% → 30%
  const topPct = 100 - i * (70 / (total - 1));
  const bottomPct = 100 - (i + 1) * (70 / (total - 1));
  const v = useCountUp(stage.value, inView, 1600, 0.05);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.9 }}
      animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ delay: i * 0.18, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="relative flex flex-col items-center group"
      data-testid={`funnel-stage-${i}`}
    >
      {/* Conversion badge between stages */}
      {i > 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.6 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ delay: i * 0.18 + 0.35, duration: 0.4 }}
          className="absolute -top-3 left-1/2 -translate-x-1/2 z-20 px-2.5 py-0.5 rounded-full bg-[#E8A33D] text-[#0F2818] text-[10px] font-bold uppercase tracking-wider shadow-md flex items-center gap-1"
        >
          <Filter className="h-2.5 w-2.5" />
          {conversion}% pass
        </motion.div>
      )}

      {/* Trapezoid stage */}
      <div
        className="relative w-full overflow-hidden"
        style={{
          clipPath: `polygon(${(100 - topPct) / 2}% 0, ${100 - (100 - topPct) / 2}% 0, ${100 - (100 - bottomPct) / 2}% 100%, ${(100 - bottomPct) / 2}% 100%)`,
        }}
      >
        {/* Stage body */}
        <div
          className="relative py-4 sm:py-5 transition-all duration-500"
          style={{
            background: `linear-gradient(135deg,
              rgba(20, 64, 31, ${0.95 - i * 0.05}) 0%,
              rgba(20, 64, 31, ${1 - i * 0.05}) 100%)`,
            boxShadow: "inset 0 1px 0 rgba(184, 217, 88, 0.15)",
          }}
        >
          {/* Animated flow particles */}
          {inView && (
            <>
              <FlowParticle delay={i * 0.5} dur={5} />
              <FlowParticle delay={i * 0.5 + 1.5} dur={5} />
              <FlowParticle delay={i * 0.5 + 3} dur={5} />
            </>
          )}

          {/* Stage content */}
          <div className="text-center px-4 relative z-10">
            <div className="font-serif-display text-2xl sm:text-3xl font-bold text-[#B8D958] leading-none tracking-tight">
              {v.toLocaleString()}
              {i === 0 ? "+" : ""}
            </div>
            <div className="mt-1 text-[9px] sm:text-[10px] uppercase tracking-[0.22em] text-white/75 font-semibold">
              {stage.label}
            </div>
          </div>

          {/* Edge glow on hover */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
               style={{ background: "radial-gradient(ellipse at center, rgba(184,217,88,0.18), transparent 70%)" }} />
        </div>
      </div>
    </motion.div>
  );
};

export default function DealFlowFunnel() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  // Conversion ratios between stages
  const conversions = FUNNEL_STAGES.map((s, i) => {
    if (i === 0) return 100;
    return Math.round((s.value / FUNNEL_STAGES[i - 1].value) * 100);
  });

  // Big "Investment" headline number for hero
  const finalVal = useCountUp(FUNNEL_STAGES[FUNNEL_STAGES.length - 1].value, inView, 1600, 0.05);

  return (
    <section
      id="deal-flow"
      data-testid="dealflow-section"
      className="bg-[#F7F8EF] relative overflow-hidden"
    >
      {/* Subtle background mesh */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none">
        <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
          <defs>
            <pattern id="dfgrid" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="#14401F" strokeWidth="0.2" />
            </pattern>
          </defs>
          <rect width="100" height="100" fill="url(#dfgrid)" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 relative" ref={ref}>
        <div className="grid lg:grid-cols-12 gap-12 items-start mb-14">
          <div className="lg:col-span-7">
            <Eyebrow>Disciplined Selection</Eyebrow>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-bold text-[#0F2818]">
              200+ Startups Screened.{" "}
              <span className="relative inline-block">
                Only the <Italic>Best</Italic>
                <svg
                  className="absolute -bottom-1 left-0 w-full"
                  height="10"
                  viewBox="0 0 200 10"
                  preserveAspectRatio="none"
                  fill="none"
                >
                  <path
                    d="M2 6 Q50 0, 100 5 T198 5"
                    stroke="#B8D958"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    style={{
                      strokeDasharray: 220,
                      strokeDashoffset: inView ? 0 : 220,
                      transition: "stroke-dashoffset 1.4s 0.5s ease-out",
                    }}
                  />
                </svg>
              </span>{" "}
              Reach IC.
            </h2>
            <p className="mt-6 text-base lg:text-lg text-[#4A5A4F] leading-relaxed max-w-2xl">
              A pipeline-to-portfolio ratio of <span className="text-[#14401F] font-bold">100:1</span>. Every cheque earns its place through six weeks of underwriting:
            </p>
            <ul className="mt-4 space-y-2 max-w-2xl">
              {[
                "Six weeks of underwriting per company — not five-minute decks",
                "Diligence on the team, market, traction signal, and capital efficiency",
                "Investment Committee gate before any cheque is wired",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-[#0F2818]/85 text-sm lg:text-base">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#B8D958] flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Big stat */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.4, duration: 0.7, type: "spring", stiffness: 80 }}
            className="lg:col-span-5"
          >
            <div className="relative rounded-3xl bg-[#14401F] text-white p-8 overflow-hidden">
              <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full bg-[#B8D958]/15 blur-2xl" />
              <div className="relative">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#B8D958] font-bold mb-3">
                  Final Investments / yr
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="font-serif-display text-7xl lg:text-8xl font-bold text-[#B8D958] leading-none">
                    {finalVal}
                  </span>
                  <span className="font-serif-display text-3xl text-[#B8D958]">+</span>
                </div>
                <p className="mt-4 text-sm text-white/70">
                  Out of <span className="text-white font-semibold">2,000+</span> sourced
                  — a <span className="text-[#E8A33D] font-semibold">1% hit rate</span>.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Funnel + criteria */}
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Funnel */}
          <div className="lg:col-span-6 relative max-w-[480px] mx-auto w-full">
            {/* Outer glow */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ delay: 0.3, duration: 1 }}
              className="absolute -inset-6 rounded-3xl pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse at center, rgba(184,217,88,0.12) 0%, transparent 60%)",
                filter: "blur(20px)",
              }}
            />

            <div className="relative space-y-1">
              {FUNNEL_STAGES.map((s, i) => (
                <FunnelStage
                  key={i}
                  stage={s}
                  i={i}
                  total={FUNNEL_STAGES.length}
                  inView={inView}
                  conversion={conversions[i]}
                />
              ))}
            </div>

            {/* Funnel base / drip */}
            <motion.div
              initial={{ opacity: 0, scaleY: 0 }}
              animate={inView ? { opacity: 1, scaleY: 1 } : {}}
              transition={{ delay: FUNNEL_STAGES.length * 0.18 + 0.4, duration: 0.6 }}
              style={{ transformOrigin: "top" }}
              className="mx-auto mt-2 w-2 h-8 bg-gradient-to-b from-[#14401F] to-[#B8D958] rounded-full"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: FUNNEL_STAGES.length * 0.18 + 0.7, duration: 0.5, type: "spring" }}
              className="mx-auto mt-2 h-4 w-4 rounded-full bg-[#B8D958] shadow-[0_0_20px_#B8D958]"
            />
          </div>

          {/* Screening criteria */}
          <div className="lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="bg-white rounded-3xl border border-[#14401F]/10 p-8 shadow-lg shadow-[#14401F]/5"
            >
              <div className="flex items-center gap-3 mb-6">
                <span className="h-8 w-8 rounded-xl bg-[#14401F] text-[#B8D958] flex items-center justify-center">
                  <Filter className="h-4 w-4" />
                </span>
                <div className="text-xs uppercase tracking-[0.25em] text-[#14401F] font-bold">
                  Screening Criteria
                </div>
              </div>
              <ul className="space-y-4">
                {SCREENING_CRITERIA.map((c, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: 16 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 0.7 + i * 0.08, duration: 0.4 }}
                    className="flex items-start gap-3 group"
                    data-testid={`screening-criterion-${i}`}
                  >
                    <span className="mt-0.5 h-5 w-5 rounded-full bg-[#DCE8C9] group-hover:bg-[#B8D958] flex items-center justify-center flex-shrink-0 transition-colors">
                      <Check className="h-3 w-3 text-[#14401F]" strokeWidth={3} />
                    </span>
                    <span className="text-[#0F2818] text-sm group-hover:text-[#14401F] transition-colors">
                      {c}
                    </span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
